import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler
);

interface Device {
  id: number;
  name: string;
  status: 0 | 1;
  socketConnected?: boolean;
  deviceTypes?: {
    id: number;
    name: string;
  };
}

interface PLCDeviceCardProps {
  device: Device;
  deviceMetrics: Record<number, Record<string, any[]>>;
  themeColor?: string;
}

// PLC-specific metric display configuration
const PLC_METRICS_CONFIG: Record<string, { label: string; unit: string; color: string; decimals?: number }> = {
  dough_temperature: { label: 'Dough Temp', unit: '°C', color: '#ff9800', decimals: 1 },
  water_temperature: { label: 'Water Temp', unit: '°C', color: '#2196f3', decimals: 1 },
  water_flow: { label: 'Water Flow', unit: 'L/min', color: '#00bcd4', decimals: 2 },
  gross_power: { label: 'Gross Power', unit: 'W', color: '#f44336', decimals: 0 },
  net_power: { label: 'Net Power', unit: 'W', color: '#e91e63', decimals: 0 },
  gross_energy: { label: 'Gross Energy', unit: 'Wh', color: '#9c27b0', decimals: 1 },
  net_energy: { label: 'Net Energy', unit: 'Wh', color: '#673ab7', decimals: 1 },
  homogenisation_power: { label: 'Homog. Power', unit: 'W', color: '#3f51b5', decimals: 0 },
  development_power: { label: 'Dev. Power', unit: 'W', color: '#2196f3', decimals: 0 },
  total_batch_time: { label: 'Batch Time', unit: 's', color: '#009688', decimals: 0 },
  homogenisation_time: { label: 'Homog. Time', unit: 's', color: '#4caf50', decimals: 0 },
  development_time: { label: 'Dev. Time', unit: 's', color: '#8bc34a', decimals: 0 },
  mixer_seq_status: { label: 'Mixer Status', unit: '', color: '#ff5722', decimals: 0 },
  mixer_seq_operation: { label: 'Mixer Op', unit: '', color: '#795548', decimals: 0 },
  // Fermentor metrics (plcFermentor.py / DB901)
  starter_co2: { label: 'Starter CO₂', unit: '%', color: '#607d8b', decimals: 2 },
  fermentor_temperature: { label: 'Fermentor Temp', unit: '°C', color: '#ff9800', decimals: 1 },
  starter_temperature: { label: 'Starter Temp', unit: '°C', color: '#ffc107', decimals: 1 },
  starter_ph: { label: 'Starter pH', unit: 'pH', color: '#8bc34a', decimals: 2 },
  starter_weight: { label: 'Starter Weight', unit: 'kg', color: '#795548', decimals: 1 },
  fermentor_seq_status: { label: 'Fermentor Status', unit: '', color: '#ff5722', decimals: 0 },
  fermentor_seq_operation: { label: 'Fermentor Op', unit: '', color: '#9e9e9e', decimals: 0 },
  fermentor_total_op_time: { label: 'Total Op Time', unit: 's', color: '#009688', decimals: 0 },
  fermentor_running_op_time: { label: 'Running Op Time', unit: 's', color: '#4caf50', decimals: 0 },
};

// Chart groups by unit type
const CHART_GROUPS: Record<string, { label: string; unit: string; color: string; metrics: string[] }> = {
  temperature: {
    label: 'Temperature',
    unit: '°C',
    color: '#ff9800',
    metrics: ['dough_temperature', 'water_temperature', 'fermentor_temperature', 'starter_temperature'],
  },
  fermentation: {
    label: 'Fermentation',
    unit: '',
    color: '#8bc34a',
    metrics: ['starter_co2', 'starter_ph', 'starter_weight'],
  },
  power: {
    label: 'Power',
    unit: 'W',
    color: '#f44336',
    metrics: ['gross_power', 'net_power', 'homogenisation_power', 'development_power'],
  },
  energy: {
    label: 'Energy',
    unit: 'Wh',
    color: '#9c27b0',
    metrics: ['gross_energy', 'net_energy'],
  },
  time: {
    label: 'Time',
    unit: 's',
    color: '#009688',
    metrics: [
      'total_batch_time',
      'homogenisation_time',
      'development_time',
      'fermentor_total_op_time',
      'fermentor_running_op_time',
    ],
  },
};

// Number of data points to show in sparkline
const SPARKLINE_POINTS = 30;

/**
 * Card component for PLC device metrics
 * Shows relevant PLC data like temperatures, power, timings with real-time chart
 */
const PLCDeviceCard: React.FC<PLCDeviceCardProps> = ({
  device,
  deviceMetrics,
  themeColor = '#ff9800',
}) => {
  // Memoize metricsData to prevent unnecessary re-renders
  const metricsData = useMemo(() =>
    deviceMetrics[device.id] || {},
    [deviceMetrics, device.id]
  );

  // Prepare chart data for each metric group
  const chartGroups = useMemo(() => {
    const groups: { key: string; label: string; unit: string; color: string; data: any }[] = [];

    Object.entries(CHART_GROUPS).forEach(([groupKey, groupConfig]) => {
      const datasets: any[] = [];

      groupConfig.metrics.forEach(metricKey => {
        const data = metricsData[metricKey];
        if (!data || !Array.isArray(data)) return;

        const config = PLC_METRICS_CONFIG[metricKey];
        if (!config) return;

        // Get last N points
        const points = data.slice(-SPARKLINE_POINTS);
        if (points.length === 0) return;

        datasets.push({
          label: config.label,
          data: points,
          borderColor: config.color,
          backgroundColor: `${config.color}20`,
          borderWidth: 1.5,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 2,
        });
      });

      // Only add group if it has data
      if (datasets.length > 0 && datasets.some(d => d.data.length > 0)) {
        groups.push({
          key: groupKey,
          label: groupConfig.label,
          unit: groupConfig.unit,
          color: groupConfig.color,
          data: {
            labels: Array(SPARKLINE_POINTS).fill(''),
            datasets,
          },
        });
      }
    });

    return groups;
  }, [metricsData]);

  // Create chart options for a specific unit
  const createChartOptions = (unit: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          boxWidth: 6,
          boxHeight: 6,
          padding: 4,
          font: { size: 8 },
          color: '#888',
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => {
            const value = context.raw?.toFixed(1) || '0';
            return `${context.dataset.label}: ${value}${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: true,
        grid: {
          color: 'rgba(255,255,255,0.05)',
        },
        ticks: {
          font: { size: 7 },
          color: '#666',
          maxTicksLimit: 3,
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  });

  const hasChartData = chartGroups.length > 0;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
      border: `1px solid ${themeColor}30`,
      borderRadius: '12px',
      padding: '1rem',
      minWidth: '280px',
    }}>
      {/* Card Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1rem',
        paddingBottom: '0.75rem',
        borderBottom: `1px solid ${themeColor}20`,
      }}>
        <div style={{
          fontSize: '1.5rem',
        }}>
          ⚙️
        </div>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: device.socketConnected ? '#00ff88' : '#ff3366',
          boxShadow: device.socketConnected
            ? '0 0 8px #00ff88'
            : '0 0 8px #ff3366',
        }} />
        <div>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#fff',
          }}>
            {device.name}
          </div>
          <div style={{
            fontSize: '0.7rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {device.deviceTypes?.name || 'PLC'}
          </div>
        </div>
      </div>

      {hasChartData ? (
        <>
          {/* Readings Section */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
            gap: '0.35rem',
            marginBottom: '0.75rem',
          }}>
            {chartGroups.flatMap(group =>
              group.data.datasets.map((ds: any, idx: number) => {
                const latestValue = ds.data[ds.data.length - 1];
                const config = Object.values(PLC_METRICS_CONFIG).find(c => c.color === ds.borderColor);
                const decimals = config?.decimals ?? 1;
                return (
                  <div
                    key={`${group.key}-${idx}`}
                    style={{
                      background: `${ds.borderColor}10`,
                      border: `1px solid ${ds.borderColor}30`,
                      borderRadius: '6px',
                      padding: '0.4rem 0.5rem',
                    }}
                  >
                    <div style={{
                      fontSize: '0.55rem',
                      color: ds.borderColor,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      marginBottom: '2px',
                    }}>
                      {ds.label}
                    </div>
                    <div style={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: '#fff',
                      fontFamily: 'monospace',
                    }}>
                      {typeof latestValue === 'number' ? latestValue.toFixed(decimals) : '0'}
                      <span style={{ fontSize: '0.55rem', opacity: 0.7, marginLeft: '2px' }}>
                        {group.unit}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Charts Section */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: chartGroups.length > 2 ? 'repeat(2, 1fr)' : '1fr',
            gap: '0.5rem',
          }}>
            {chartGroups.map(group => (
              <div
                key={group.key}
                style={{
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                  padding: '0.5rem',
                  border: `1px solid ${group.color}20`,
                }}
              >
                {/* Chart Title */}
                <div style={{
                  fontSize: '0.55rem',
                  color: group.color,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  marginBottom: '0.25rem',
                }}>
                  {group.label}
                </div>
                {/* Chart */}
                <div style={{ height: '70px' }}>
                  <Line data={group.data} options={createChartOptions(group.unit)} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{
          textAlign: 'center',
          color: '#6c757d',
          fontSize: '0.85rem',
          padding: '1rem',
        }}>
          No metrics available
        </div>
      )}
    </div>
  );
};

export default PLCDeviceCard;
